import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import {
  Reservation,
  ReservationRecurOptions,
  ReservationRecurResponse,
  ReservationCustomerRecurrencePreview,
  ReservationEmailType,
} from '../models/reservation';
import { Upload } from '../models/upload';

export class ReservationService extends BaseService<Reservation> {
  override model = Reservation;

  override modelName = 'reservation';
  override modelLabel = 'Reservations';
  override modelApiVersion = 2;

  // Static API Methods

  async getWithDate(
    id: string,
    date: number
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${id}`,
      query: { date: date.toString() },
    });

    const response = await this.requestItem<Reservation>(request, Reservation);
    if (response.data) {
      (response.data as any).saturated = new Date();
    }
    return response;
  }

  async unconfirmed(
    { page = 1, limit = 20 }: { page?: number; limit?: number } = {},
  ): Promise<ApiListResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/unconfirmed`,
      query: {
        page: page.toString(),
        limit: limit.toString(),
      },
    });

    return await this.requestList<Reservation>(request, Reservation);
  }

  async recurOptions(): Promise<ApiResponse<ReservationRecurOptions>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/recurOptions`,
    });

    return await this.requestItem<ReservationRecurOptions>(request, ReservationRecurOptions);
  }

  // Instance-style API Methods (take reservation ID + date)

  async saveReservation(
    reservation: Reservation,
    { future = false, overrideTime = false }: { future?: boolean; overrideTime?: boolean } = {},
  ): Promise<ApiResponse<Reservation>> {
    const isNew = reservation.isNewRecord;

    reservation.overrideTime = overrideTime;

    if (isNew) {
      const request = new ApiRequest({
        method: 'POST',
        url: `${this.modelBaseUrl}/${reservation.objectID()}`,
        data: reservation.toJSON(),
      });
      return await this.requestItem<Reservation>(request, Reservation);
    }

    const date = (reservation.date as any)?.integer ?? reservation.date;
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${reservation.objectID()}`,
      data: reservation.toJSON(),
      query: {
        date: date?.toString() ?? '',
        future: future ? '1' : '0',
      },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async cancel(
    reservationId: string,
    date: number,
    {
      future = false,
      sendEmail = false,
      cancellationTypeId,
      cancellationNote,
    }: {
      future?: boolean;
      sendEmail?: boolean;
      cancellationTypeId?: number;
      cancellationNote?: string;
    } = {}
  ): Promise<ApiResponse<Reservation>> {
    const query: Record<string, string> = {
      date: date.toString(),
      sendEmail: sendEmail ? '1' : '0',
      future: future ? '1' : '0',
    };

    if (cancellationTypeId != null) query.type = cancellationTypeId.toString();
    if (cancellationNote) query.note = cancellationNote;

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/cancel`,
      query,
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async unCancel(reservationId: string, date: number): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/cancel`,
      query: { date: date.toString(), undo: '1' },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async deleteReservation(
    reservationId: string,
    date: number,
    { future = false }: { future?: boolean } = {}
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/delete`,
      query: { date: date.toString(), future: future ? '1' : '0' },
    });

    return await this.request(request);
  }

  async unDelete(reservationId: string, date: number): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/delete`,
      query: { date: date.toString(), undo: '1' },
    });

    return await this.request(request);
  }

  async restoreReservation(reservationId: string, date: number): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/restore`,
      query: { date: date.toString(), undo: '1' },
    });

    return await this.request(request);
  }

  async uploads(
    reservationId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<Upload>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/uploads`,
      query: { page: page.toString() },
    });

    return await this.requestList<Upload>(request, Upload);
  }

  async checkIn(
    reservationId: string,
    { findPass = false, customerPasses }: { findPass?: boolean; customerPasses?: any } = {}
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationId}/checkIn`,
      data: { findPass, customerPasses },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async unCheckIn(reservationId: string): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/checkIn`,
      query: { undo: '1' },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async didNotShow(reservationId: string): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/noShow`,
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async unDidNotShow(reservationId: string): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/noShow`,
      query: { undo: '1' },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async confirm(
    reservationId: string,
    { sendEmail = false }: { sendEmail?: boolean } = {}
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/confirm`,
      query: { sendEmail: sendEmail ? '1' : '0' },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async addService(
    reservationId: string,
    {
      serviceId,
      employeeId,
      price,
      duration,
      processing,
      finishing,
      future = false,
    }: {
      serviceId: number;
      employeeId: number;
      price: number;
      duration: number;
      processing: number;
      finishing: number;
      future?: boolean;
    }
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/addService`,
      query: {
        service: serviceId.toString(),
        employee: employeeId.toString(),
        duration: duration.toString(),
        price: price.toString(),
        processing: processing.toString(),
        finishing: finishing.toString(),
        future: future ? '1' : '0',
      },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async updateService(
    reservationId: string,
    params: {
      reservationServiceId: number;
      serviceId: number;
      employeeId: number;
      price: number;
      date: number;
      startTime: number;
      duration: number;
      processing: number;
      finishing: number;
      future?: boolean;
    }
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/updateService`,
      query: {
        id: reservationId,
        reservationService: params.reservationServiceId.toString(),
        service: params.serviceId.toString(),
        employee: params.employeeId.toString(),
        price: params.price.toString(),
        date: params.date.toString(),
        startTime: params.startTime.toString(),
        duration: params.duration.toString(),
        processing: params.processing.toString(),
        finishing: params.finishing.toString(),
        future: params.future ? '1' : '0',
      },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async removeService(
    reservationId: string,
    reservationServiceId: number,
    { future = false }: { future?: boolean } = {}
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/removeService`,
      query: {
        reservationService: reservationServiceId.toString(),
        future: future ? '1' : '0',
      },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async previewCustomerRecurrence(
    reservationId: string,
    { customerId, date }: { customerId?: number; date?: number }
  ): Promise<ApiResponse<ReservationCustomerRecurrencePreview>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationId}/previewCustomerRecurrence`,
      data: { customer: customerId, date },
    });

    return await this.requestItem<ReservationCustomerRecurrencePreview>(
      request,
      ReservationCustomerRecurrencePreview
    );
  }

  async recurCustomers(
    reservationId: string,
    { reservationCustomerIds, reservations }: { reservationCustomerIds: number[]; reservations: any }
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationId}/recurCustomers`,
      data: {
        reservationCustomers: reservationCustomerIds,
        reservations,
      },
    });

    return await this.request(request);
  }

  async recur(
    reservation: Reservation
  ): Promise<ApiResponse<ReservationRecurResponse>> {
    const date = (reservation.date as any)?.integer ?? reservation.date;

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservation.objectID()}/recur`,
      data: reservation.toJSON(),
      query: { date: date?.toString() ?? '' },
    });

    return await this.requestItem<ReservationRecurResponse>(request, ReservationRecurResponse);
  }

  async email(
    reservationId: string,
    date: number,
    type: ReservationEmailType = ReservationEmailType.received
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/email`,
      query: { date: date.toString(), type },
    });

    return await this.request(request);
  }

  async assignCustomerPasses(
    reservationId: string,
    passes: any
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationId}/assignCustomerPass`,
      data: { passes },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async applyVouchers(
    reservationId: string,
    vouchers: any
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationId}/applyVoucher`,
      data: { vouchers },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async integrationAction(
    reservationId: string,
    date: number,
    { guid, actionCode }: { guid: string; actionCode: string }
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationId}/integrationAction`,
      query: { date: date.toString(), guid, action: actionCode },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  async processAutomation(
    reservationId: string,
    serviceAutomationId: number
  ): Promise<ApiResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationId}/processAutomation`,
      data: { serviceAutomation: serviceAutomationId },
    });

    return await this.requestItem<Reservation>(request, Reservation);
  }

  /**
   * Fetch all reservations by IDs, paginating through all pages.
   * Returns a flat array of all Reservation items.
   */
  async listIDs(ids: (string | number)[]): Promise<Reservation[]> {
    const allItems: Reservation[] = [];
    const seenIds = new Set<string>();
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const request = new ApiRequest({
        method: 'POST',
        url: `${this.modelBaseUrl}/ids`,
        data: { ids },
        query: { page: page.toString() },
      });

      const response = await this.requestList<Reservation>(request, Reservation);

      const items = response.items || [];
      items.forEach((item) => {
        const itemId = item.objectID();

        if (!itemId) {
          allItems.push(item);
          return;
        }

        if (seenIds.has(itemId)) { return; }

        seenIds.add(itemId);
        allItems.push(item);
      });

      hasMore = response.paging
        ? page < response.paging.pageCount
        : items.length >= 20;
      page++;
    }

    return allItems;
  }
}
